// Copyright (c) 2019 by Michael Marucheck
//
// Use of this source code is governed by an MIT-style license that
// can be found in the LICENSE file distributed with this file.

import React, { useState, useEffect } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import "react-day-picker/lib/style.css";

import { Supervisor } from "../store/schema";
import { getApi, volCache } from "../store/api";
import { encouragement } from "../util";
import {
  useAsyncGuard,
  update,
  observe,
  useWidthGate,
  Lifecycle,
} from "../UiUtil";
import { URLS, breadcrumbs } from "../urls";
import { OrganizationChooserDetails } from "../components/OrganizationChooser";
import { NavBar, navLink } from "../components/NavBar";
import { SaveButton } from "../components/SaveButton";
import {
  Concern,
  validate,
  ONE_OR_MORE_NAME_VALIDATORS,
  ValidatedTextInputDetail,
  UID_VALIDATORS,
  EMAIL_VALIDATORS,
  PHONE_NUMBER_VALIDATORS,
  ValidatedPhoneInputDetail,
} from "../components/ValidatedUI";
import { PageContainer } from "../components/PageContainer";
import { DeleteButton } from "../components/DeleteButton";
import { CancelButton } from "../components/CancelButton";
import { Breadcrumbs } from "../components/BreadCrumb";

const WIDTH_GATE = {
  wideNav: 600,
};

const Page: React.FC<RouteComponentProps<{ uid: string }>> = ({
  history,
  match,
}) => {
  debug("======= SupervisorDetailPage =======");
  const uid = match.params.uid;
  const widthBucket = useWidthGate(WIDTH_GATE);
  const [state, setState] = useState(volCache().get());
  const [lifecycle, setLifecycle] = useState(Lifecycle.VIEWED);
  const edited = () => setLifecycle(Lifecycle.EDITED);
  const [formUid, setFormUid] = useState(uid);

  const { user, loaded, organizations, school, supervisors, volunteer } = state;
  const supervisorDoc = supervisors.find(x => x.data.uid === uid);
  const supervisor = supervisorDoc != null ? supervisorDoc.data : null;
  const schoolUid = school != null ? school.data.uid : "";
  const volunteerUid = user != null ? user.uid : "";

  const initName = (supervisor && supervisor.name) || "";
  const initEmail = (supervisor && supervisor.email) || "";
  const initPhone = (supervisor && supervisor.phone) || "";
  const initOrgUid = (supervisor && supervisor.organizationUid) || "";
  const initIsDeleted = (supervisor && supervisor.isDeleted) || false;

  const [name, setName] = observe(edited, useState<string>(initName));
  const [email, setEmail] = observe(edited, useState<string>(initEmail));
  const [phone, setPhone] = observe(edited, useState<string>(initPhone));
  const [organizationUid, setOrganizationUid] = observe(
    edited,
    useState<string>(initOrgUid)
  );
  const [isDeleted, setIsDeleted] = observe(edited, useState(initIsDeleted));

  if (uid !== formUid) {
    setFormUid(uid);
    setLifecycle(Lifecycle.VIEWED);
    initializeForm(supervisor || emptySupervisor());
  }

  const api = getApi();
  const guard = useAsyncGuard();

  const concerns: { [key: string]: Concern | null } = {
    name: validate(ONE_OR_MORE_NAME_VALIDATORS, name),
    email: validate(EMAIL_VALIDATORS, email),
    phone: validate(PHONE_NUMBER_VALIDATORS, phone),
    organizationUid: validate(UID_VALIDATORS, organizationUid),
  };

  useEffect(() => {
    return volCache().subscribe(newState => {
      if (update("setState", state, setState, newState)) {
        if (lifecycle !== Lifecycle.EDITED) {
          const oDoc = newState.supervisors.find(x => x.data.uid === uid);
          initializeForm(oDoc == null ? emptySupervisor() : oDoc.data);
          debug(`state changed, updated form to match new state`);
        } else {
          debug(`state changed but form is edited, ignoring update`);
        }
      } else {
        debug(`volCache notified of update but nothing changed`);
      }
    });
  });

  function emptySupervisor(): Supervisor {
    return {
      uid,
      schoolUid,
      volunteerUid,
      name: "",
      email: "",
      phone: "",
      organizationUid: "",
    };
  }

  function initializeForm(supervisor: Supervisor) {
    update("name", name, setName, supervisor.name);
    update("email", email, setEmail, supervisor.email);
    update("phone", phone, setPhone, supervisor.phone);
    update(
      "organizationUid",
      organizationUid,
      setOrganizationUid,
      supervisor.organizationUid
    );
    setLifecycle(Lifecycle.VIEWED);
  }

  function parseForm(): Supervisor {
    return {
      uid,
      schoolUid,
      volunteerUid,
      name,
      email,
      phone,
      organizationUid,
    };
  }

  function blockDelete() {
    if (
      state.activities.some(
        x => x.data.supervisorUid === uid && !x.data.isDeleted
      )
    ) {
      window.alert("You have activities that still reference this supervisor");
      return true;
    }
    return false;
  }

  async function updateIsDeleted(value: boolean) {
    setIsDeleted(value);
    await saveSupervisor({ ...parseForm(), isDeleted: value });
  }

  async function save() {
    await saveSupervisor(parseForm());
    history.push(URLS.vSupervisorsDetail(uid));
  }

  async function saveSupervisor(supervisor: Supervisor) {
    await guard(api.volunteer.saveSupervisor(supervisor));
  }

  return (
    <PageContainer
      title="Edit Supervisor"
      crumbs={breadcrumbs().vSupervisors}
      administrators={state.administrators}
      school={state.school}
      checks={{ user, loaded, volunteer }}
    >
      {user && loaded && volunteer && (
        <div className="details-form">
          <div className="details">
            <ValidatedTextInputDetail
              htmlId="name"
              label="Name:"
              value={name}
              onChange={setName}
              concern={concerns.name}
            />
            <ValidatedTextInputDetail
              htmlId="email"
              label="Email:"
              type="email"
              value={email}
              onChange={setEmail}
              concern={concerns.email}
            />
            <ValidatedPhoneInputDetail
              htmlId="phone"
              label="Phone:"
              value={phone}
              onChange={setPhone}
              concern={concerns.phone}
            />
            <OrganizationChooserDetails
              organizations={organizations}
              organizationUid={organizationUid}
              onChangeOrganization={setOrganizationUid}
              className="detail"
            />
          </div>
          <div className="done">
            <SaveButton
              lifecycle={lifecycle}
              setLifecycle={setLifecycle}
              concerns={concerns}
              onSave={save}
            />
            <DeleteButton
              blockDelete={blockDelete}
              isDeleted={isDeleted}
              updateIsDeleted={updateIsDeleted}
              lifecycle={lifecycle}
              setLifecycle={setLifecycle}
            />
            <CancelButton
              lifecycle={lifecycle}
              setLifecycle={setLifecycle}
              onCancel={() => initializeForm(supervisor || emptySupervisor())}
            />
          </div>
          {lifecycle === Lifecycle.SAVED && (
            <>
              <div className="nav">
                {encouragement()} Here are some ideas for where to go next:
              </div>
              <NavBar
                wide={widthBucket >= WIDTH_GATE.wideNav}
                links={[
                  navLink(URLS.index(), "home"),
                  navLink(URLS.vSupervisors(), "Supervisors"),
                  navLink(URLS.vOrganizations(), "Organizations"),
                ]}
              />
            </>
          )}
        </div>
      )}
      {lifecycle === Lifecycle.VIEWED && (
        <>
          <div className="section"></div>
          <Breadcrumbs crumbs={breadcrumbs().vSupervisors} />
        </>
      )}
    </PageContainer>
  );
};
export const SupervisorDetailPage = withRouter(Page);

function debug(message: string) {
  if (true) {
    console.log(`SupervisorDetailPage: ${message}`);
  }
}
